import * as React from 'react';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  CssBaseline,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const GITHUB_DARK = {
  bg: '#0d1117',
  surface: '#161b22',
  border: '#30363d',
  text: '#f0f6fc',
  muted: '#8b949e',
  link: '#58a6ff',
  green: '#238636',
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: GITHUB_DARK.link },
    success: { main: GITHUB_DARK.green },
    background: { default: GITHUB_DARK.bg, paper: GITHUB_DARK.surface },
    divider: GITHUB_DARK.border,
    text: { primary: GITHUB_DARK.text, secondary: GITHUB_DARK.muted },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif',
    h1: { fontWeight: 750, letterSpacing: -0.6 },
    h2: { fontWeight: 700, letterSpacing: -0.4 },
    button: { textTransform: 'none', fontWeight: 650 },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(13, 17, 23, 0.72)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${GITHUB_DARK.border}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${GITHUB_DARK.border}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10 },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: { color: GITHUB_DARK.link },
      },
    },
  },
});

function Section({ id, eyebrow, title, subtitle, children }) {
  const showEyebrow = Boolean(eyebrow && eyebrow !== title);
  return (
    <Box component="section" id={id} sx={{ py: { xs: 6, md: 9 } }}>
      <Container maxWidth="lg">
        <Stack spacing={2} sx={{ mb: 4 }}>
          {showEyebrow ? (
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', letterSpacing: 1.6 }}
            >
              {eyebrow}
            </Typography>
          ) : null}
          {title ? (
            <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 36 } }}>
              {title}
            </Typography>
          ) : null}
          {subtitle ? (
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: 820,
                fontSize: { xs: 16, md: 18 },
                lineHeight: 1.7,
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Stack>
        {children}
      </Container>
    </Box>
  );
}

export default function App() {
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const nav = [
    { label: 'About', href: '#about' },
    { label: 'All Projects', href: '/projects' },
    { label: 'Insights', href: '/insights' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          backgroundImage:
            'radial-gradient(900px 500px at 20% 0%, rgba(88,166,255,0.18), rgba(13,17,23,0)), radial-gradient(700px 450px at 85% 10%, rgba(35,134,54,0.12), rgba(13,17,23,0))',
        }}
      >
        <AppBar position="sticky" elevation={0}>
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ gap: 2, py: 1 }}>
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ flexGrow: 1 }}
              >
                <Typography
                  component="a"
                  href="/"
                  variant="subtitle1"
                  sx={{
                    backgroundImage: 'linear-gradient(90deg, #1f6feb, #58A6FF)',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                    textDecoration: 'none',
                    fontWeight: 820,
                    fontSize: 20,
                    letterSpacing: -0.2,
                  }}
                >
                  TZY‑Lab
                </Typography>
              </Stack>

              {isMdUp ? (
                <Stack direction="row" spacing={1}>
                  {nav.map((item) => (
                    <Button
                      key={item.label}
                      component="a"
                      href={item.href}
                      color="inherit"
                      variant="text"
                      sx={{ color: 'text.secondary' }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Stack>
              ) : (
                <>
                  <IconButton
                    aria-label="Open navigation menu"
                    onClick={() => setDrawerOpen(true)}
                    sx={{ color: 'text.primary' }}
                  >
                    <Box component="span" sx={{ fontSize: 22, lineHeight: 1 }}>
                      ≡
                    </Box>
                  </IconButton>
                  <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    PaperProps={{
                      sx: {
                        width: 280,
                        backgroundColor: 'background.paper',
                        borderLeft: `1px solid ${GITHUB_DARK.border}`,
                      },
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <Typography sx={{ fontWeight: 750, mb: 1 }}>
                        Navigate
                      </Typography>
                      <Divider sx={{ mb: 1.5 }} />
                      <Stack spacing={1}>
                        {nav.map((item) => (
                          <Button
                            key={item.label}
                            component="a"
                            href={item.href}
                            onClick={() => setDrawerOpen(false)}
                            variant="text"
                            sx={{
                              justifyContent: 'flex-start',
                              color: 'text.secondary',
                            }}
                          >
                            {item.label}
                          </Button>
                        ))}
                      </Stack>
                    </Box>
                  </Drawer>
                </>
              )}
            </Toolbar>
          </Container>
        </AppBar>

        <Box component="main">
          <Box
            component="section"
            sx={{
              pt: { xs: 7, md: 10 },
              pb: 7,
              minHeight: { xs: 'auto', md: '80vh' },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Container maxWidth="lg">
              <Grid
                container
                spacing={6}
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: 36, sm: 44, md: 54 },
                      lineHeight: 1.08,
                      mb: 2,
                    }}
                  >
                    Engineering Solutions for the Modern Business.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: 16, md: 18 },
                      lineHeight: 1.75,
                      mb: 3,
                    }}
                  >
                    I&apos;m Tam, founder of Mindlink Tec Ltd. At TZY Lab, I
                    design, build, and deploy digital systems that turn complex
                    challenges into scalable results.
                  </Typography>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{ alignItems: { sm: 'center' } }}
                  >
                    <Button
                      component="a"
                      href="/projects"
                      variant="contained"
                      color="success"
                      size="large"
                    >
                      Explore My Projects
                    </Button>
                    <Button
                      component="a"
                      href="/contact"
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: 'divider',
                        color: 'text.primary',
                        '&:hover': { borderColor: 'primary.main' },
                      }}
                    >
                      Let&apos;s Collaborate
                    </Button>
                  </Stack>

                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', mt: 2.5 }}
                  >
                    Fast experiments, durable systems, measurable outcomes.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/tam-founder-tzy-lab-workspace.gif`}
                    alt="Tam, founder of TZY Lab, working on digital systems and automation tools"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: { xs: 300, sm: 400, md: 450 },
                      objectFit: 'contain',
                      borderRadius: 3,
                      border: `1px solid ${GITHUB_DARK.border}`,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      mb: 3,
                    }}
                  />
                  <Card
                    sx={{
                      borderLeft: `4px solid ${GITHUB_DARK.link}`,
                      background:
                        'linear-gradient(135deg, rgba(22,27,34,1) 0%, rgba(13,17,23,1) 100%)',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography sx={{ fontWeight: 750, mb: 1 }}>
                        TZY Lab
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                      >
                        Digital systems, tools, and growth experiments—built
                        with a pragmatic engineering mindset.
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Stack spacing={1}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Operated by <strong>Mindlink Tec Ltd</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Contact:{' '}
                          <Link href="mailto:tam@tzylab.com">tam@tzylab.com</Link>
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          </Box>

          <Divider sx={{ borderColor: 'divider' }} />

          <Section
            id="focus"
            eyebrow="Focus"
            title="What We Experiment With"
            subtitle="From data automation to digital growth, my work focuses on creating systems that provide long-term leverage."
          >
            <Grid container spacing={2.25}>
              {[
                {
                  title: 'Tooling & Automation',
                  desc: 'Custom-built software and spreadsheets to eliminate manual overhead.',
                  href: '/projects/tools',
                },
                {
                  title: 'Strategic Growth',
                  desc: 'Data-backed frameworks to optimize your business visibility and reach.',
                  href: '/projects/strategy',
                },
                {
                  title: 'Experimental Projects',
                  desc: 'Unique solutions for niche business requirements.',
                  href: '/projects/experiments',
                },
              ].map((card) => (
                <Grid key={card.title} item xs={12} md={4}>
                  <Card
                    sx={{
                      height: '100%',
                      transition: 'transform 120ms ease, border-color 120ms ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <CardActionArea
                      component="a"
                      href={card.href}
                      sx={{ height: '100%', alignItems: 'stretch' }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontWeight: 750, mb: 1.25 }}>
                          {card.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          {card.desc}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ color: 'primary.main', fontWeight: 650 }}
                          >
                            Explore →{'\u00A0'}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Section>

          <Divider sx={{ borderColor: 'divider' }} />

          <Section
            id="about"
            eyebrow="About"
            title="Built by Tam"
            subtitle="I am a technologist and problem solver. Through Mindlink Tec Ltd, I bridge the gap between technical complexity and business growth."
          >
            <Card
              sx={{
                background:
                  'linear-gradient(135deg, rgba(22,27,34,1) 0%, rgba(13,17,23,1) 100%)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2.5} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography sx={{ fontWeight: 750, mb: 1 }}>
                      Mindlink Tec Ltd
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                      Building systems that reduce operational drag, improve decision speed, and
                      compound business leverage over time.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack spacing={1.25} sx={{ alignItems: { md: 'flex-end' } }}>
                      <Button
                        component="a"
                        href="https://www.linkedin.com/in/neablister-tam-90b5b6142/"
                        target="_blank"
                        rel="noopener"
                        variant="outlined"
                        sx={{
                          borderColor: 'divider',
                          color: 'text.primary',
                          '&:hover': { borderColor: 'primary.main' },
                          width: { xs: '100%', md: 'auto' },
                        }}
                      >
                        Read my journey on LinkedIn
                      </Button>
                      <Button
                        component="a"
                        href="/contact"
                        variant="contained"
                        color="success"
                        sx={{ width: { xs: '100%', md: 'auto' } }}
                      >
                        Work with me
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Section>
        </Box>

        <Divider sx={{ borderColor: 'divider' }} />

        <Box component="footer" sx={{ py: 5 }}>
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                © 2026 TZY Lab | Operated by Mindlink Tec Ltd
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Contact: <Link href="mailto:tam@tzylab.com">tam@tzylab.com</Link>
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Social:{" "}
                  <Link
                    href="https://www.linkedin.com/in/neablister-tam-90b5b6142/"
                    target="_blank"
                    rel="noopener"
                  >
                    LinkedIn
                  </Link>
                </Typography>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

